import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
export const PRODUCT_IMPORT_COLUMNS = [
    {
        key: "article_code",
        label: "Article code",
        required: true,
        type: "string",
        example: "FNT-300",
        description: "Unique product code. Re-uploads update the matching product.",
    },
    {
        key: "name",
        label: "Name",
        required: true,
        type: "string",
        example: "Fanta Orange 300ml",
        description: "Product display name.",
    },
    {
        key: "category",
        label: "Category",
        required: false,
        type: "category",
        example: "Beverages",
        description: "Category name; created automatically if new.",
    },
    { key: "brand", label: "Brand", required: false, type: "string", example: "Fanta", description: "" },
    {
        key: "sub_brand",
        label: "Sub-brand",
        required: false,
        type: "string",
        example: "",
        description: "",
    },
    {
        key: "variant_name",
        label: "Variant",
        required: false,
        type: "string",
        example: "Orange",
        description: "",
    },
    {
        key: "pack_size",
        label: "Pack size",
        required: false,
        type: "string",
        example: "300ml",
        description: "",
    },
    {
        key: "external_ref",
        label: "External ref",
        required: false,
        type: "string",
        example: "SAP-99102",
        description: "Your ERP/SAP id.",
    },
    {
        key: "pack_width_mm",
        label: "Width (mm)",
        required: false,
        type: "int",
        example: "65",
        description: "",
    },
    {
        key: "pack_height_mm",
        label: "Height (mm)",
        required: false,
        type: "int",
        example: "120",
        description: "",
    },
    {
        key: "pack_depth_mm",
        label: "Depth (mm)",
        required: false,
        type: "int",
        example: "65",
        description: "",
    },
    {
        key: "pack_weight_g",
        label: "Weight (g)",
        required: false,
        type: "int",
        example: "330",
        description: "",
    },
    {
        key: "packaging_type",
        label: "Packaging type",
        required: false,
        type: "string",
        example: "can",
        description: "",
    },
    {
        key: "packaging_label",
        label: "Packaging label",
        required: false,
        type: "string",
        example: "",
        description: "",
    },
    {
        key: "orientation",
        label: "Orientation",
        required: false,
        type: "string",
        example: "upright",
        description: "",
    },
    {
        key: "is_priority",
        label: "Priority",
        required: false,
        type: "boolean",
        example: "false",
        description: "true/false",
    },
    {
        key: "is_active",
        label: "Active",
        required: false,
        type: "boolean",
        example: "true",
        description: "true/false (default true)",
    },
    // ── Image URL columns ────────────────────────────────────────────────
    // Multiple URLs per cell are pipe-separated: `https://a.png|https://b.png`.
    // Pipe (not comma) because URL query strings commonly contain commas;
    // pipes are vanishingly rare and unambiguous in CSV/XLSX cells.
    //
    // The face-typed columns (image_url_front etc.) let the operator declare
    // which face each shot represents. The generic image_urls column defaults
    // face_type to 'unknown' — the embedding worker will then derive the real
    // face from the vision response's `view` field and backfill it. Operators
    // can mix both styles in the same row.
    {
        key: "image_urls",
        label: "Image URLs",
        required: false,
        type: "string",
        example: "https://cdn.example.com/fnt300-front.jpg|https://cdn.example.com/fnt300-back.jpg",
        description: "Pipe-separated URLs. View defaults to 'unknown' (vision will auto-classify).",
    },
    {
        key: "image_url_front",
        label: "Image URL (front)",
        required: false,
        type: "string",
        example: "",
        description: "Pipe-separated URLs of front-face shots.",
    },
    {
        key: "image_url_back",
        label: "Image URL (back)",
        required: false,
        type: "string",
        example: "",
        description: "Pipe-separated URLs of back-face shots.",
    },
    {
        key: "image_url_left",
        label: "Image URL (left)",
        required: false,
        type: "string",
        example: "",
        description: "Pipe-separated URLs of left-side shots.",
    },
    {
        key: "image_url_right",
        label: "Image URL (right)",
        required: false,
        type: "string",
        example: "",
        description: "Pipe-separated URLs of right-side shots.",
    },
    {
        key: "image_url_top",
        label: "Image URL (top)",
        required: false,
        type: "string",
        example: "",
        description: "Pipe-separated URLs of top shots.",
    },
    {
        key: "image_url_bottom",
        label: "Image URL (bottom)",
        required: false,
        type: "string",
        example: "",
        description: "Pipe-separated URLs of bottom shots.",
    },
    {
        key: "price", label: "Price", required: false, type: "number", example: "18.00",
        description: "Product selling price, zero or greater. Blank leaves the current value unchanged.",
    },
    {
        key: "mrp", label: "MRP", required: false, type: "number", example: "20.00",
        description: "Optional product MRP, zero or greater. Blank leaves the current value unchanged.",
    },
    {
        key: "currency", label: "Currency", required: false, type: "string", example: "INR",
        description: "Three letters, normalized to uppercase. Defaults to INR when price is present.",
    },
];
/**
 * Canonical face-typed image URL columns + the view they imply. Exported so
 * the worker and any future consumer iterate the same list.
 */
export const PRODUCT_IMPORT_IMAGE_URL_COLUMNS = [
    { key: "image_urls", viewType: "unknown" },
    { key: "image_url_front", viewType: "front" },
    { key: "image_url_back", viewType: "back" },
    { key: "image_url_left", viewType: "left" },
    { key: "image_url_right", viewType: "right" },
    { key: "image_url_top", viewType: "top" },
    { key: "image_url_bottom", viewType: "bottom" },
];
export const productImportStatus = z.enum([
    "queued",
    "processing",
    "completed",
    "failed",
    "cancelled",
]);
export const productImportInfo = z.object({
    id: z.string().uuid(),
    status: productImportStatus,
    format: z.enum(["csv", "xlsx"]),
    originalFilename: z.string().nullable(),
    totalRows: z.number().int(),
    okRows: z.number().int(),
    failedRows: z.number().int(),
    // ── Image-upload phase (phase 2). total_images is set once all rows are
    // processed; uploaded/failed are incremented per product-image.fetch job.
    totalImages: z.number().int(),
    uploadedImages: z.number().int(),
    failedImages: z.number().int(),
    // ── Embedding phase (phase 3, informational). Derived at read time from
    // embedding_jobs (request.bulk_import_id), NOT a gate on `status` — an import
    // reads "completed" after images, while embeddings keep draining in the
    // background. `pending` = queued + processing.
    embeddings: z.object({
        total: z.number().int(),
        completed: z.number().int(),
        failed: z.number().int(),
        pending: z.number().int(),
    }),
    errorReportUrl: z.string().nullable(), // presigned, when present
    errorSummary: z.string().nullable(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
});
export const productImportsListResponse = pagedResponse(productImportInfo);
export const listProductImportsQuery = paginationQuerySchema;
export const createProductImportResponse = productImportInfo;
export const cancelProductImportResponse = productImportInfo;
//# sourceMappingURL=product-imports.schemas.js.map